import { ContentItemViewNodesType } from '../types'

/**
 * Node type classifications for BOD/Manufacturer logic
 */
export enum NodeClassification {
    BOD_PILL = 'bod-pill',
    BOD_PARAGRAPH = 'bod-paragraph',
    MANUFACTURER_PILL = 'manufacturer-pill',
    MANUFACTURER_PARAGRAPH = 'manufacturer-paragraph',
    OTHER = 'other',
}

/**
 * Classifies a node into one of the predefined categories
 */
export const classifyNode = (
    node: Partial<ContentItemViewNodesType>
): NodeClassification => {
    const isBODPill = node.id?.includes('_bod_') === true
    const isManufacturerNode = node.isManufacturers === true
    const isBODNode = node.isBOD === true

    // BOD pills are identified by _bod_ in their ID
    if (isBODPill) {
        return NodeClassification.BOD_PILL
    }

    // Mixed paragraph (both BOD and Manufacturer flags)
    if (isManufacturerNode && isBODNode) {
        return NodeClassification.MANUFACTURER_PARAGRAPH
    }

    // BOD paragraph
    if (isBODNode && !isManufacturerNode) {
        return NodeClassification.BOD_PARAGRAPH
    }

    // Manufacturer elements - distinguish between paragraphs and pills by ID pattern
    if (isManufacturerNode && !isBODNode) {
        const looksLikePill =
            node.id?.includes('pill') ||
            (node.id?.includes('_') && node.id !== 'manufacturer-para')
        return looksLikePill
            ? NodeClassification.MANUFACTURER_PILL
            : NodeClassification.MANUFACTURER_PARAGRAPH
    }

    return NodeClassification.OTHER
}

/**
 * Checks if there are conflicts between BOD and Manufacturer paragraph states
 */
export const checkForConflicts = (
    nodes: Partial<ContentItemViewNodesType>[]
): { isValid: boolean; conflicts: string[] } => {
    const activeBODParagraphs = nodes.filter(
        (node) =>
            classifyNode(node) === NodeClassification.BOD_PARAGRAPH &&
            !node.isHidden
    )

    const activeManufacturerParagraphs = nodes.filter(
        (node) =>
            classifyNode(node) === NodeClassification.MANUFACTURER_PARAGRAPH &&
            !node.isHidden
    )

    if (
        activeBODParagraphs.length > 0 &&
        activeManufacturerParagraphs.length > 0
    ) {
        return {
            isValid: false,
            conflicts: [
                ...activeBODParagraphs.map((n) => n.id || 'unknown'),
                ...activeManufacturerParagraphs.map((n) => n.id || 'unknown'),
            ],
        }
    }

    return { isValid: true, conflicts: [] }
}

/**
 * Calculates the visibility of a node based on BOD/Manufacturer reference node logic
 */
export const calculateNodeVisibility = (
    node: Partial<ContentItemViewNodesType>,
    referenceNode: Partial<ContentItemViewNodesType>,
    shouldBeHidden: boolean
): boolean => {
    const isReferenceBOD = referenceNode.isBOD === true
    const isReferenceManufacturer = referenceNode.isManufacturers === true

    // Early return if reference node is neither BOD nor Manufacturer
    if (!isReferenceBOD && !isReferenceManufacturer) {
        return node.isHidden ?? false
    }

    const currentIsHidden = node.isHidden ?? false
    let newIsHidden = currentIsHidden

    // Determine if this is a BOD pill
    const isBODPill = node.id?.includes('_bod_') === true
    const isManufacturerNode = node.isManufacturers === true
    const isBODNode = node.isBOD === true

    if (isReferenceBOD) {
        // Handle nodes when BOD paragraph is toggled
        if (isManufacturerNode && !isBODNode) {
            // Pills logic when BOD is toggled
            if (isBODPill) {
                // BOD pills follow BOD paragraph state
                newIsHidden = shouldBeHidden
            } else {
                // Manufacturer pills: deactivate when BOD is activated, stay unchanged when BOD is deactivated
                if (!shouldBeHidden) {
                    // BOD is being activated - deactivate Manufacturer pills
                    newIsHidden = true
                } else {
                    // BOD is being deactivated - leave Manufacturer pills unchanged
                    newIsHidden = currentIsHidden
                }
            }
        } else if (isManufacturerNode && isBODNode) {
            // Manufacturer paragraphs: only deactivate when BOD is activated, always leave unchanged when BOD is deactivated
            if (!shouldBeHidden) {
                // BOD is being activated - deactivate Manufacturer
                newIsHidden = true
            } else {
                // BOD is being deactivated - leave Manufacturer unchanged
                newIsHidden = currentIsHidden
            }
        } else if (isBODNode) {
            // BOD paragraphs match the shouldBeHidden state
            newIsHidden = shouldBeHidden
        }
    } else if (isReferenceManufacturer) {
        // Handle nodes when Manufacturer paragraph is toggled
        if (isManufacturerNode && !isBODNode) {
            // Pills logic when Manufacturer is toggled
            if (isBODPill) {
                // BOD pills: deactivate when Manufacturer is activated, stay unchanged when Manufacturer is deactivated
                if (!shouldBeHidden) {
                    // Manufacturer is being activated - deactivate BOD pills
                    newIsHidden = true
                } else {
                    // Manufacturer is being deactivated - leave BOD pills unchanged
                    newIsHidden = currentIsHidden
                }
            } else {
                // Manufacturer pills follow Manufacturer paragraph state
                newIsHidden = shouldBeHidden
            }
        } else if (isBODNode) {
            // BOD paragraphs: only deactivate when Manufacturer is activated, leave unchanged when Manufacturer is deactivated
            if (!shouldBeHidden) {
                // Manufacturer is being activated - deactivate BOD
                newIsHidden = true
            } else {
                // Manufacturer is being deactivated - leave BOD unchanged
                newIsHidden = currentIsHidden
            }
        } else if (isManufacturerNode && isBODNode) {
            // Manufacturer paragraphs match the shouldBeHidden state
            newIsHidden = shouldBeHidden
        }
    }

    return newIsHidden
}

/**
 * Checks if a BOD pill should be hidden based on its associated BOD paragraph
 * Returns null if not a BOD pill, boolean if it is a BOD pill
 */
export const shouldHideBODPill = (
    contentItemView: Partial<ContentItemViewNodesType>,
    contentItemViewNodes: Partial<ContentItemViewNodesType>[],
    dtvItemId?: string
): boolean | null => {
    if (classifyNode(contentItemView) !== NodeClassification.BOD_PILL) {
        return null
    }

    const bodParagraph = contentItemViewNodes.find(
        (civ) => civ.dtvItemId === contentItemView.refBodItemId
    )
    const isBodParagraphDisabled = bodParagraph?.isHidden ?? true

    // BOD pills should be hidden when BOD paragraph is disabled OR when item is explicitly hidden
    return !!dtvItemId || isBodParagraphDisabled
}

/**
 * Synchronizes visibility between BOD and Manufacturer nodes with validation
 */
export const syncVisibilityBetweenBODAndManufacturers = (
    referenceNode: Partial<ContentItemViewNodesType> | null | undefined,
    allNodes: Partial<ContentItemViewNodesType>[] | null | undefined,
    shouldBeHidden: boolean
): Partial<ContentItemViewNodesType>[] => {
    if (!referenceNode || !allNodes) {
        return allNodes || []
    }

    const isReferenceBOD = referenceNode.isBOD === true
    const isReferenceManufacturer = referenceNode.isManufacturers === true

    // Early return if reference node is neither BOD nor Manufacturer
    if (!isReferenceBOD && !isReferenceManufacturer) {
        return allNodes
    }

    // Find current state of BOD and Manufacturer paragraphs
    const bodParagraph = allNodes.find(
        (n) => n?.isBOD === true && n?.isManufacturers !== true
    )
    const manufacturerParagraph = allNodes.find(
        (n) => n?.isManufacturers === true && n?.isBOD !== true
    )
    const isBODActive = bodParagraph ? !(bodParagraph.isHidden ?? false) : false
    const isManufacturerActive = manufacturerParagraph
        ? !(manufacturerParagraph.isHidden ?? false)
        : false

    //When deactivating Manufacturer paragraph, activate BOD paragraph
    const shouldActivateBODOnManufacturerDeactivation =
        isReferenceManufacturer &&
        shouldBeHidden &&
        isManufacturerActive &&
        !isBODActive

    const updatedNodes = allNodes.map((node) => {
        const currentIsHidden = node.isHidden ?? false
        let newIsHidden

        if (shouldActivateBODOnManufacturerDeactivation) {
            // Activate BOD elements when deactivating sole active Manufacturer
            const isBODElement =
                (node?.isBOD === true && node?.isManufacturers !== true) ||
                (node?.isManufacturers === true && node?.id?.includes('_bod_'))
            const isManufacturerElement =
                node?.isManufacturers === true && !node?.id?.includes('_bod_')

            if (isBODElement) {
                newIsHidden = false // Activate BOD elements
            } else if (isManufacturerElement) {
                newIsHidden = true // Deactivate Manufacturer elements
            } else {
                newIsHidden = currentIsHidden // Leave others unchanged
            }
        } else {
            newIsHidden = calculateNodeVisibility(
                node,
                referenceNode,
                shouldBeHidden
            )
        }

        return newIsHidden !== currentIsHidden
            ? { ...node, isHidden: newIsHidden }
            : node
    })

    // Check for conflicts after update
    const validation = checkForConflicts(updatedNodes)
    if (!validation.isValid) {
        console.warn(
            'Conflict detected: BOD and Manufacturer paragraphs cannot both be active. Conflicting nodes:',
            validation.conflicts
        )
    }

    return updatedNodes
}
